/**
 * Store Implementation Module
 *
 * `reactiveStore()` — a plain object whose top-level property writes fan out to
 * subscribers, with computed keys built from other keys (`derive`) and a manual
 * re-fire for an in-place mutation the Proxy's `set` trap can't see (`notify`).
 * `internalData` below is the library's instance of it; external apps build
 * their own the same way (as-home's homeStore).
 *
 * The subscribe half — per-key, multi-key, whole-store, coalescing and
 * batching — is not implemented here. It comes from store/subscriptions.ts, which is
 * the one subscription system behind every store in the library: this one,
 * appData (data/projectManagement.ts), paramStore, sysData and the derived
 * stores `deriveFromStore` returns.
 */

import type { IReactiveStore, DeriveSpec, DerivedStore } from "../as-components";
import type { Project } from "../data/projectManagement.ts";
import type { StoredNotification } from '../data/data-interfaces.ts';

import { DEFAULT_ASSETS_PATH, DEFAULT_SERVER_ROOT, DEV_CDN_ROOT, fullAppSuiteRootURL, localDev, isOnPrem, useLocalAssets, type Deployment } from "../data/constants.ts";
import { warnDeprecated } from "../util/deprecatedEl.ts";
import { createSource, type Source } from "./subscriptions.ts";
import { addAsGlobals } from "../asGlobals.ts";
import { sendEv } from '../data/events.ts';

//TODO:@REMOVE — moved into store/subscriptions.ts alongside the rest of the subscribe
// machinery. Re-exported because that is where callers have always found it.
export { createKeyedNotifier } from './subscriptions.ts';


/**
 * Builds a computed store from another store's keys -- shared implementation
 * behind reactiveStore()'s inline `deriveSpec`, its `.derive()` method, and
 * currentProjectData.derive() (projectManagement.ts).
 *
 * - Each key recomputes when any of its `deps` changes, and is cached in between.
 * - Reading a key never triggers a recompute by itself.
 * - Recompute happens once per commit on the watched source, however many deps
 *   moved together. `phase` on internalData has eight of them.
 *
 * `own` decides where the recomputed keys are announced:
 *
 * - Pass the watched source itself (reactiveStore's inline `deriveSpec`) and
 *   the derived keys join the same commit their dependencies caused. They are
 *   then ordinary keys on that store — no second notifier, and no branch in
 *   `subscribeKey` to route them anywhere special.
 * - Leave it out (`.derive()`, which can be called any number of times with
 *   overlapping key names) and the derived store gets a source of its own, so
 *   two derived stores that both define `isReady` don't collide.
 */
export function deriveFromStore<Spec extends Record<string, DeriveSpec<any>>>(
  watch: Source<any>,
  getKey: (key: string) => any,
  spec: Spec,
  own?: Source<any>
): DerivedStore<Spec> {
  const cache = {} as Record<string, any>;
  /** dep key -> the derived keys computed from it. */
  const dependents = new Map<string, string[]>();

  const compute = (key: string): any => spec[key].get(spec[key].deps.map(getKey));

  for (const key of Object.keys(spec)) {
    cache[key] = compute(key);
    for (const dep of spec[key].deps) {
      const list = dependents.get(dep);
      if (list) list.push(key); else dependents.set(dep, [key]);
    }
  }

  const target: Source<any> = own ?? createSource({
    name: 'DERIVED',
    snapshot: () => ({ ...cache }),
    resolve: (key: any) => cache[key as string],
    keysOf: (key: any) => [key as string],
  });

  watch.onCommit(changed => {
    const stale = new Set<string>();
    for (const dep of changed) for (const key of dependents.get(dep) ?? []) stale.add(key);
    if (stale.size === 0) return;

    const moved: string[] = [];
    for (const key of stale) {
      const next = compute(key);
      if (cache[key] === next) continue;
      cache[key] = next;
      moved.push(key);
    }
    if (moved.length === 0) return;

    // Joining the caller's commit (own === watch) is a set insert; a store of
    // our own needs a commit of its own, nested inside this one.
    if (own === watch) for (const key of moved) changed.add(key);
    else target.commit(moved);
  });

  const methods = {
    subscribeKey: (key: string, cb: (value: any, key: string) => void) => target.subscribe(key, cb),
  };

  return new Proxy(methods, {
    get(t, prop) {
      if (typeof prop === 'symbol' || prop in t) return Reflect.get(t, prop);
      return cache[prop as string];
    },
  }) as any;
}

/**
 * Creates a reactive store with the provided schema.
 *
 * @param schema Initial store schema with default values
 * @param deriveSpec Optional computed keys, defined inline instead of via a
 *   follow-up `.derive({...})` call. Each one depends on this same store's
 *   own keys and is readable and subscribable straight off the returned proxy,
 *   exactly like a plain key -- callers don't need to keep a second object
 *   around for the derived half, and a subscriber naming a plain key and a
 *   derived one gets a single call when both move.
 * @returns A proxy object exposing the schema (+ any derived keys) plus
 *   subscribeKey/subscribeKeys/subscribeAll/batch/notify/derive
 */
export function reactiveStore<T extends Record<string, any>, D extends Record<string, DeriveSpec<any>> = {}>(
  schema: T,
  deriveSpec?: D
): IReactiveStore & T & { readonly [K in keyof D]: ReturnType<D[K]['get']> } {
  let proxy: IReactiveStore & T & { readonly [K in keyof D]: ReturnType<D[K]['get']> };
  let derived: DerivedStore<D> | undefined;

  //TODO:@REMOVE — bookkeeping for the pre-0.16.95 store API aliases below.
  const legacyEffects = new Map<string, Map<(value: any) => void, () => void>>();
  const legacySubscribers = new Map<any, Array<() => void>>();
  let emitUpdateEvent = true;

  // Subscribers, coalescing and batching all come from here -- see store/subscriptions.ts.
  // Reads go through `proxy` rather than the raw schema so derived keys resolve
  // the same way plain ones do. The whole-store snapshot IS the proxy: it is
  // live and free, where copying 30-odd keys on every commit would not be (and
  // would call the `imgDir` getter for nothing).
  const source = createSource<typeof proxy>({
    name: 'STORE',
    snapshot: () => proxy,
    resolve: (target) => Array.isArray(target)
      ? target.map(key => (proxy as any)[key])
      : (proxy as any)[target],
    keysOf: (target) => (Array.isArray(target) ? target : [target]) as string[],
  });

  // Copied by descriptor, not spread. A spread would call any getter in the
  // schema once and keep the answer, which quietly turned `get imgDir()` into
  // a fixed string decided at module-eval — so writing useLocalAssets did
  // nothing. Descriptors keep a getter a getter.
  const storeObj: any = Object.defineProperties({
    /** React to one key. Derived keys included — they are keys like any other. */
    subscribeKey(key: string, cb: (value: any, key?: string) => void): () => void {
      return source.subscribe(key, cb);
    },
    /** React to several keys at once, with one call however many moved together. */
    subscribeKeys(keys: readonly string[], cb: (values: any[], keys: readonly string[]) => void): () => void {
      return source.subscribe(keys, cb);
    },
    /** React to any change. The callback gets `(store, changedKeys)`. */
    subscribeAll(cb: (store: typeof proxy, changedKeys: ReadonlySet<string>) => void): () => void {
      return source.subscribe(cb);
    },
    /** Apply several writes as one change: subscribers see the end state, once. */
    batch<R>(fn: () => R): R {
      return source.batch(fn);
    },
    //TODO:@REMOVE — pre-0.16.95 store API, kept as warning aliases so external
    // consumers (e.g. as-home builds against older source) don't break. Every
    // method below forwards to subscribeKey; remove the whole block plus the
    // legacyEffects/legacySubscribers/emitUpdateEvent bookkeeping above once
    // nothing warns any more.
    /** @deprecated Use subscribeKey() — same behavior, same unsubscribe return. */
    addSideEffect(key: string, cb: (value: any) => void): () => void {
      warnDeprecated('store.addSideEffect', '[STORE] addSideEffect() is deprecated — use subscribeKey() (same signature, same unsubscribe return).');
      const unsub = storeObj.subscribeKey(key, cb);
      let effects = legacyEffects.get(key);
      if (!effects) { effects = new Map(); legacyEffects.set(key, effects); }
      effects.set(cb, unsub);
      return () => { unsub(); legacyEffects.get(key)?.delete(cb); };
    },
    /** @deprecated Keep the unsubscribe function subscribeKey() returns instead. */
    removeSideEffect(key: string, cb: (value: any) => void): boolean {
      warnDeprecated('store.removeSideEffect', '[STORE] removeSideEffect() is deprecated — keep the unsubscribe function subscribeKey() returns instead.');
      const effects = legacyEffects.get(key);
      const unsub = effects?.get(cb);
      if (!unsub) return false;
      unsub();
      effects!.delete(cb);
      return true;
    },
    /** @deprecated Keep the unsubscribe functions subscribeKey() returns instead. */
    clearSideEffects(key: string): boolean {
      warnDeprecated('store.clearSideEffects', '[STORE] clearSideEffects() is deprecated — keep the unsubscribe functions subscribeKey() returns instead.');
      const effects = legacyEffects.get(key);
      if (!effects || effects.size === 0) return false;
      effects.forEach(unsub => unsub());
      legacyEffects.delete(key);
      return true;
    },
    /** @deprecated Use subscribeKey() per key, or subscribeKeys() for several.
     *  Registers `component.updateSubbedValues({key: value})` callbacks for each
     *  prop; returns a snapshot of the current values. */
    subscribe(component: any, props: string[]): { [key: string]: any } {
      warnDeprecated('store.subscribe', '[STORE] subscribe(component, props) is deprecated — use subscribeKey(key, cb), or subscribeKeys(keys, cb) for several at once.');
      let unsubs = legacySubscribers.get(component);
      if (!unsubs) { unsubs = []; legacySubscribers.set(component, unsubs); }
      for (const prop of props) {
        unsubs.push(storeObj.subscribeKey(prop, (v: any) => component.updateSubbedValues({ [prop]: v })));
      }
      return props.reduce((acc: Record<string, any>, prop) => { acc[prop] = proxy[prop]; return acc; }, {});
    },
    /** @deprecated Pairs with the deprecated subscribe(); removes all of a component's subscriptions. */
    unsubscribe(component: any): void {
      warnDeprecated('store.unsubscribe', '[STORE] unsubscribe(component) is deprecated — keep the unsubscribe functions subscribeKey() returns instead.');
      legacySubscribers.get(component)?.forEach(unsub => unsub());
      legacySubscribers.delete(component);
    },
    /** @deprecated Only gates the window 'as-intrn-store-update' event; per-key subscribers still fire. */
    get emitUpdateEvent(): boolean { return emitUpdateEvent; },
    set emitUpdateEvent(v: boolean) {
      warnDeprecated('store.emitUpdateEvent', "[STORE] emitUpdateEvent is deprecated — it only gates the window 'as-intrn-store-update' event; per-key subscribers fire regardless.");
      emitUpdateEvent = v;
    },
    //TODO:@REMOVE — end of deprecated alias block.
    notify(key: string): void {
      source.commit([key]);
    },
    derive<Spec extends Record<string, DeriveSpec<any>>>(spec: Spec): DerivedStore<Spec> {
      return deriveFromStore(source, key => proxy[key], spec);
    },
  }, Object.getOwnPropertyDescriptors(schema));

  proxy = new Proxy(storeObj, {
    get(target, prop) {
      if (typeof prop !== 'symbol' && deriveSpec && (prop as string) in deriveSpec) {
        return (derived as any)[prop as string];
      }
      return Reflect.get(target, prop);
    },
    set(target, prop, value) {
      target[prop as keyof typeof target] = value;
      source.commit([prop as string]);
      return true;
    }
  }) as any;

  // Built last: getKey reads through `proxy` and the source is already live, so
  // derived keys can depend on this same store's own schema keys. Passing
  // `source` twice is what puts them in its keyspace rather than a separate one.
  if (deriveSpec) {
    derived = deriveFromStore(source, key => proxy[key], deriveSpec, source);
  }

  //TODO:@REMOVE — with the emitUpdateEvent block above. Fires after the keyed
  // subscribers, same order as before batching existed. In a batch it goes out
  // once per changed key at the end.
  source.subscribe((_store: any, changedKeys: ReadonlySet<string>) => {
    if (!emitUpdateEvent) return;
    for (const key of changedKeys) {
      sendEv(window, 'as-intrn-store-update', {
        detail: { store: proxy, prop: key, value: proxy[key] }
      });
    }
  });

  return proxy;
}

/**
 * One rolled-up label for what the app is doing — see `internalData.phase`.
 * Lives here rather than in sysDataStore.ts because the schema below needs it
 * and sysDataStore imports this file; sysDataStore re-exports both for the
 * public API.
 */
export type SysPhase =
  | 'starting'          // as-app is up, the configurator isn't ready yet
  | 'ready'             // idle and usable
  | 'session-lost'      // the server session died — recovery flow is running
  | 'restoring-session' // a saved session is being pushed back to the server
  | 'loading-project'   // between as-before-project-loaded and as-after-project-loaded
  | 'saving-project'
  | 'configuring'       // a CAD rebuild is in flight
  | 'busy';             // something else holds a busy key

/** The active step, as reported by `<as-step-selector>`. Nested rather than
 *  three flat fields so "is there a step selector at all" is one check. */
export interface StepSelectorState {
  /** Id of the active step panel. */
  id: string;
  /** Zero-based index of the active step. */
  index: number;
  /** How many steps there are, for a "step 2 of 5" label. */
  count: number;
}

/**
 * internalData — the library's own state
 *
 * One store for everything as-components tracks about the running app, in five
 * groups. Flat on purpose: the Proxy's `set` trap only sees top-level writes, so
 * `internalData.session.id = 'x'` would notify nobody. Grouping is what the
 * section headers below and the read-only views (see store/sysDataStore.ts) are
 * for.
 *
 * The counterpart is `appData` (data/projectManagement.ts) — the app's own
 * state, which is persisted with the project. Nothing here is.
 */
export const internalData = reactiveStore({
  // Environment & assets =====================================================
  serverRoot: DEFAULT_SERVER_ROOT,
  assetsPath: DEFAULT_ASSETS_PATH,
  useLocalAssets: useLocalAssets,
  get imgDir() {
    // srcdoc iframes (as-code-demo): window.location is 'about:srcdoc'.
    // A <base href> tag points at the parent page's directory, so resolving
    // '.' against document.baseURI gives the correct asset root.
    // Regular same-origin iframes (e.g. grabcadTester) must NOT use this path —
    // their document.baseURI is a full URL with filename/query that produces
    // a broken path when used as a directory.
    if (window.location.protocol === 'about:') {
      return `${new URL('.', document.baseURI).href}${this.assetsPath}/img`;
    }
    if (this.useLocalAssets) return `/${this.assetsPath}/img`;
    // On-prem serves its own assets, even from a LAN address.
    if (localDev && !isOnPrem) return `${DEV_CDN_ROOT}/${this.assetsPath}/img`;
    return `${fullAppSuiteRootURL}/${this.assetsPath}/img`
  },
  /** <as-app app-id>. */
  appId: undefined,
  /** Which host the app is embedded in, e.g. 'grabcad'. */
  appEnv: null as string | null,
  /** Where the suite is installed: 'cloud', or 'onprem' on a customer's own
   *  server. Read by apps as sysData.deployment. */
  deployment: 'cloud' as Deployment,
  appLangFileName: undefined,
  /** <as-app app-version> — the app's own version, if it set one. */
  appVersion: undefined as string | undefined,
  /** The as-components version. */
  componentsVersion: undefined as string | undefined,
  /** Which Paramate tier the configurator resolved to. */
  tier: undefined as string | undefined,

  // Session & auth ===========================================================
  // features/serverApi/asApi.ts is the only writer of these three: getUserData() sets
  // pendingAuth around the fetch and settles the other two in every branch.
  /** The signed-in user, or null. */
  userData: null,
  /** The user is signed in. False until the first answer lands, and false when
   *  the check never got one (getUserData's transportFailed). */
  isAuthenticated: false as boolean,
  /** A getUserData() call is in flight. */
  pendingAuth: true as boolean,
  /** The boot auth gate is up: the app UI is hidden until the sign-in check
   *  resolves, so an unauthenticated visitor never sees it before being
   *  redirected. <as-app> owns it and reflects it to its `auth-checking`
   *  attribute, which is what the CSS keys off. This is the gate, not the
   *  check: pendingAuth goes true again on every keepalive refetch, while this
   *  goes down once and stays down. */
  authGateUp: false as boolean,
  /** The Paramate session id. <as-configurator> is the only writer. */
  sessionId: undefined as string | undefined,
  /** A saved session is being restored onto the server. */
  sessionRestoring: false as boolean,
  /** The server session died (expiry, 404). */
  sessionLost: false as boolean,
  /** The Paramate session is live. */
  configuratorReady: false as boolean,
  // Single source of truth for the active UI language. LocalizationController
  // (localization-mixin.ts) is the only writer; anything that needs to show
  // or react to the current language (e.g. as-lang-selector) should read/
  // subscribe here instead of keeping its own separately-synced copy.
  language: 'en' as string,

  // UI =======================================================================
  uiInputs: {} as Record<string, any>,
  /** The active step, or undefined with no <as-step-selector>. */
  stepSelector: undefined as StepSelectorState | undefined,
  /** Capped at MAX_STORED_NOTIFICATIONS. */
  notificationHistory: [] as StoredNotification[],
  /** Set to true when IndexedDB is unavailable (e.g. iOS Lockdown Mode). */
  noDb: false as boolean,

  // Project ==================================================================
  currentProject: null as Project | null,
  unsavedChanges: false as boolean,
  // Whether the current app may save files locally. Set from the appList's
  // per-app downloadEnabled flag (license-gated) once as-app knows its appId.
  canDownload: true as boolean,
  /** Between as-before-project-loaded and as-after-project-loaded. */
  projectLoading: false as boolean,
  /** A real file save is running (not the debounced autosave). */
  projectSaving: false as boolean,

  // Activity =================================================================
  // What the library is currently doing. These and the session/project flags
  // above are written only through _setSysData() (store/sysDataStore.ts),
  // which is also where the read-only `app.sysData` view over them lives —
  // that view is the supported way for an app to read them.
  /** <as-app> has finished its init queue. */
  appReady: false as boolean,
  /** A CAD rebuild is in flight. */
  configuring: false as boolean,
  /** The aggregate ref-counted busy state — same answer as `as-app-busy`. */
  busy: false as boolean,
  /** Which keys are holding it busy. */
  busyKeys: [] as string[],
  /** An export (download, GrabCAD transfer, ...) is in flight. */
  exporting: false as boolean,
  /** The undo stack has something to undo. Same answer as `as-history-changed`. */
  canUndo: false as boolean,
  /** The undo stack has something to redo. */
  canRedo: false as boolean,
}, {
  // Derived keys — readable and subscribable straight off internalData, same
  // as a plain key. These are why the sysData view holds no state of its own:
  // the project fields it reports are computed from what is already here
  // rather than mirrored into a second copy.
  /** One rolled-up label for "what is the app doing", in priority order — a
   *  lost session outranks a project load, which outranks a background
   *  configure. Binding this beats binding four booleans and combining them. */
  phase: {
    deps: ['sessionLost', 'sessionRestoring', 'projectLoading', 'projectSaving',
           'configuring', 'appReady', 'configuratorReady', 'busy'],
    get: ([sessionLost, sessionRestoring, projectLoading, projectSaving,
           configuring, appReady, configuratorReady, busy]: any[]): SysPhase => {
      if (sessionLost) return 'session-lost';
      if (sessionRestoring) return 'restoring-session';
      if (projectLoading) return 'loading-project';
      if (projectSaving) return 'saving-project';
      if (configuring) return 'configuring';
      if (!appReady || !configuratorReady) return 'starting';
      if (busy) return 'busy';
      return 'ready';
    },
  },
  projectName: {
    deps: ['currentProject'],
    get: ([project]: any[]) => (project as Project | null)?.metadata?.name,
  },
  projectId: {
    deps: ['currentProject'],
    get: ([project]: any[]) => (project as Project | null)?.metadata?.id,
  },
  /** Unsaved changes, as a plain boolean. */
  projectDirty: {
    deps: ['unsavedChanges'],
    get: ([dirty]: any[]) => !!dirty,
  },
});

// ============================================================================
// Debug globals
// ============================================================================

addAsGlobals({
  internalData,
  currentProjectData: null,
  // Debug hook: override the license-gated download flag (default true).
  setCanDownload: (value = true) => { internalData.canDownload = value; },
});

